import { useEffect, useState } from 'react'
import clsx from 'clsx'
import { HelpCircle } from 'react-feather'
import dayjs from 'dayjs'
import { CircularProgress } from '@material-ui/core'
import { Button, Card, Divider, Icon, Text } from '@/components'
import { AlertMessage } from 'app-components'
import Battery from '@/app/features/electric-vehicles/charging-history/Battery'
import GasSavingsDialog from '@/authenticated/components/GasSavingsDialog'
import { useNavigation } from '@/app/hooks'
import { useAppDispatch, useAppSelector } from '@/hooks'
import { plugSessionsCollection } from '@/reducers/plugSessionCollections'
import DateRangePicker from '@/app/components/DateRangePicker'
import FeatureFeedbackBanner from '@/app/components/FeatureFeedback'
import { PlugSession, PlugSessions } from '@/types/plugSessions'
import {
  activeChargeHours,
  formatHoursDuration,
  formatSessionDates,
  isLevel1Session,
} from './PlugSessionDetail'
import useViewModel from './useViewModel'
import { MISSED_CHARGE_TARGET_HELP_URL } from '@/utils/helpCenterUrls'

function formatCurrency(amount: number, currency: string) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(
    amount,
  )
}

// Headline totals drop the cents ($127, not $127.43) — the summary cards are
// a glance, and the per-session rows below carry the exact figures
function formatWholeCurrency(amount: number, currency: string) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(amount)
}

// Multi-day disambiguation lives in the date row (formatSessionDates renders
// "November 10–12"); the session length is the Duration stat, so the time
// range stays plain
function formatTimeRange(start: string, end: string) {
  return `${dayjs(start).format('h:mm A')} – ${dayjs(end).format('h:mm A')}`
}

// Filter-chip date: "Nov 8" in the current year, "Nov 8, 2025" otherwise
function formatChipDate(date: string | null) {
  const d = dayjs(date)
  return d.isSame(dayjs(), 'year') ? d.format('MMM D') : d.format('MMM D, YYYY')
}

type PresetKey = 'all' | 'month' | 'past30'

const PRESET_LABELS: Record<PresetKey, string> = {
  all: 'All Time',
  month: 'This Month',
  past30: 'Past 30 Days',
}

// Computed per render so a session left open overnight rolls the ranges over
function presetRanges(): Record<Exclude<PresetKey, 'all'>, [string, string]> {
  const today = dayjs().format('YYYY-MM-DD')
  return {
    month: [dayjs().startOf('month').format('YYYY-MM-DD'), today],
    past30: [dayjs().subtract(30, 'day').format('YYYY-MM-DD'), today],
  }
}

type PresetChipProps = {
  label: string
  active: boolean
  onClick: () => void
}

function PresetChip({ label, active, onClick }: PresetChipProps) {
  return (
    <button
      onClick={onClick}
      // No global preflight: strip UA button chrome explicitly on both arms
      className={clsx(
        'rounded-full px-3 py-1 cursor-pointer',
        active
          ? 'bg-themed-grey-900 border-0'
          : 'bg-white border border-solid border-themed-grey-200',
      )}
    >
      <Text
        variant="body4"
        className={active ? '!text-themed-white' : '!text-themed-grey-500'}
      >
        {label}
      </Text>
    </button>
  )
}

/**
 * The blue info banner (same treatment as DR events on the home page),
 * rendered under the stats row whenever the session missed its charge target.
 * A doomed session (analyzer: mathematically impossible to hit the limit in
 * the plugged-in time) coaches "plug in earlier" instead of the generic copy.
 */
function MissedTargetAlert({ session }: { session: PlugSession }) {
  const { charge_limit_doomed, charge_target_percent, ready_by_time } = session
  // Fold the limit and Ready By time into the doomed copy when known:
  // "…your charge limit of 80% by 6:00 AM."
  const limitPhrase = charge_target_percent
    ? ` of ${charge_target_percent}%${
        ready_by_time ? ` by ${ready_by_time}` : ''
      }`
    : ''
  return (
    <AlertMessage variant="info">
      {charge_limit_doomed
        ? `There wasn't enough time to hit your charge limit${limitPhrase}. `
        : "Your battery didn't hit its charge limit. "}
      <a
        href={MISSED_CHARGE_TARGET_HELP_URL}
        target="_blank"
        rel="noreferrer"
        className="underline underline-offset-2 cursor-pointer !text-inherit"
        // Inside a clickable card — don't also open the detail
        onClick={(event) => event.stopPropagation()}
      >
        Learn more.
      </a>
    </AlertMessage>
  )
}

function NoRatePlanAlert({ onAddRatePlan }: { onAddRatePlan?: () => void }) {
  return (
    <AlertMessage variant="info">
      {'To see what each charge costs, '}
      <span
        className="underline underline-offset-2 cursor-pointer"
        onClick={onAddRatePlan}
      >
        add your electricity rate plan.
      </span>
    </AlertMessage>
  )
}

type SessionStatProps = {
  label: string
  children: React.ReactNode
}

function SessionStat({ label, children }: SessionStatProps) {
  return (
    <div className="flex flex-col gap-0.5 min-w-0">
      {/* Labels can wrap: on narrow screens wrapping beats overflowing the
          card, and min-w-0 lets the column shrink below its content */}
      <Text variant="body4">{label}</Text>
      {/* mt-auto bottom-aligns values across columns when one label wraps */}
      <Text variant="body1" className="tabular-nums mt-auto">
        {children}
      </Text>
    </div>
  )
}

type PlugSessionCardProps = {
  session: PlugSession
  showCosts: boolean
  onSelect?: (session: PlugSession) => void
}

function PlugSessionCard({
  session,
  showCosts,
  onSelect,
}: PlugSessionCardProps) {
  const {
    start_time,
    end_time,
    total_cost,
    energy_kwh,
    currency,
    missed_charge_target,
  } = session

  // Every card opens the detail page: even without hourly telemetry the L2
  // shows data the card doesn't (exact times, battery %, rates, blended
  // cost) — hiding the CTA on ~7% of sessions read as randomly broken
  const clickable = !!onSelect

  return (
    <Card
      fullWidth
      onClick={clickable ? () => onSelect(session) : undefined}
      // box-border: this app has no global border-box reset (preflight off), so
      // Card's w-full + p-6 overflows its parent without it
      className={clsx('box-border', clickable && 'cursor-pointer')}
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-themed-green-100 rounded-lg flex items-center justify-center shrink-0">
            <Icon name="BatteryCharging" size={20} color="green-700" />
          </div>
          <div className="flex flex-col gap-0.5 shrink-0">
            <Text variant="body1">{formatSessionDates(session)}</Text>
            {/* Charge window, not plug window — plug-out detection for
                sleeping cars runs hours late (see formatSessionDates) */}
            <Text variant="body4" className="whitespace-nowrap">
              {formatTimeRange(
                session.charge_start_time ?? start_time,
                session.charge_end_time ?? end_time,
              )}
            </Text>
          </div>
          {showCosts && total_cost !== null && (
            <div className="flex flex-1 items-center justify-end gap-1 min-w-0">
              <Text
                variant="subheader"
                className="whitespace-nowrap tabular-nums"
              >
                {formatCurrency(total_cost, currency)}
              </Text>
            </div>
          )}
        </div>

        <Divider />

        {/* Charger-only sessions have no SoC (a wall charger can't read the
            car's battery) — hide the battery instead of an empty shell. The
            header row already shows the time range */}
        {session.start_battery_percent !== null &&
          session.end_battery_percent !== null && (
            <Battery charge={session} hideTimes bordered />
          )}

        {/* Charged For = the charge records' clock time, so it always agrees
            with the header's charge window (energy ÷ rate famously didn't:
            a 5h10m window displayed as 5h26m). Energy/active-rate is only
            the fallback for telemetry-only sessions with no charge records.
            Matches the L2's Active Charge Time row, which shares the logic. */}
        <div className="flex gap-2 justify-between">
          {activeChargeHours(session) !== null && (
            <SessionStat label="Charged For">
              {formatHoursDuration(activeChargeHours(session) as number)}
            </SessionStat>
          )}
          <SessionStat label="Energy Used">{energy_kwh} kWh</SessionStat>
        </div>

        {/* L1 (120 V) charging often can't finish overnight regardless of
            behavior — the missed-limit coaching never applies */}
        {missed_charge_target && !isLevel1Session(session) && (
          <MissedTargetAlert session={session} />
        )}

        {/* Charger session correlated to a car charge — attribute it. The
            name is either the car's display name or "your {year} {make}
            {model}", composed by the BE */}
        {session.charged_vehicle_name && (
          <AlertMessage variant="info">
            {`This session was to charge ${session.charged_vehicle_name}.`}
          </AlertMessage>
        )}

        {/* Tap affordance: a labeled action row at the card's foot, in the
            design system's blue-link treatment (blue on white clears WCAG AA;
            the earlier purple label on a purple tint did not). A bare chevron
            read as decoration, so customers didn't know the card was tappable.
            body1 = 14px, matching the stat text; the chart glyph previews the
            hourly detail behind the tap. */}
        {clickable && (
          <>
            <Divider />
            <div className="flex items-center justify-center gap-1.5">
              <Icon name="BarChart2" size={16} color="blue-500" />
              <Text
                variant="body1"
                className="!text-themed-blue-500 font-semibold"
              >
                View Charge Details
              </Text>
              <Icon name="ChevronRight" size={16} color="blue-500" />
            </div>
          </>
        )}
      </div>
    </Card>
  )
}

/**
 * Summary card beside Total Spent: how many sessions the filtered range
 * holds and the total time plugged in across them, rounded to whole hours.
 */
function SessionsSummaryCard({ sessions }: { sessions: PlugSessions }) {
  // Average ACTIVE charging time per session (charge-record durations, not
  // plugged-in time) to the nearest half hour; 13 renders "13", not "13.0"
  // (JS numbers drop the trailing .0 on their own)
  const averageHours = sessions.count
    ? Math.round(sessions.total_active_charge_seconds / sessions.count / 1800) /
      2
    : 0
  return (
    <Card fullWidth className="box-border">
      {/* h-auto: same stretch fix as TotalSpentCard */}
      <div className="flex flex-col gap-1 h-auto items-center text-center">
        <Text variant="body1">Total Charges</Text>
        <Text variant="h1" className="tabular-nums">
          {sessions.count.toLocaleString('en-US')}
        </Text>
        <Text variant="body2">
          {`Avg. Charge Time: ${averageHours} hr${
            averageHours === 1 ? '' : 's'
          }`}
        </Text>
      </div>
    </Card>
  )
}

/**
 * Section header card: the range's total spend, plus savings vs. an equivalent
 * gas car. The info icon opens the same GasSavingsDialog used by the legacy
 * Total Spent card, so gas-car details stay editable in one place.
 */
function TotalSpentCard({ sessions }: { sessions: PlugSessions }) {
  // Average cost per hour of ACTIVE charging (same basis as Avg. Charge
  // Time) — total spend over charge-record hours in the filtered range
  const chargeHours = sessions.total_active_charge_seconds / 3600
  const averagePerHour =
    sessions.total_cost !== null && chargeHours > 0
      ? sessions.total_cost / chargeHours
      : null
  // Average cost per kWh: the number customers compare against their rate
  // plan. Same totals as the Total Energy card, so total / energy reconciles
  const averagePerKwh =
    sessions.total_cost !== null && sessions.total_energy_kwh > 0
      ? sessions.total_cost / sessions.total_energy_kwh
      : null
  const averages = [
    averagePerHour !== null
      ? `${formatCurrency(
          Math.round(averagePerHour * 100) / 100,
          sessions.currency,
        )} / hr`
      : null,
    averagePerKwh !== null
      ? `${formatCurrency(
          Math.round(averagePerKwh * 100) / 100,
          sessions.currency,
        )} / kWh`
      : null,
  ].filter((average): average is string => average !== null)

  return (
    <Card fullWidth className="box-border">
      {/* h-auto: Card's content wrapper is h-full, which would stretch this
          short content and leave dead space below the subline */}
      <div className="flex flex-col gap-1 h-auto items-center text-center">
        <Text variant="body1">Total Spent</Text>
        <Text variant="h1" className="tabular-nums">
          {formatCurrency(sessions.total_cost ?? 0, sessions.currency)}
        </Text>
        {/* Always one line: "Avg: $0.25 / hr • $0.04 / kWh" */}
        {averages.length > 0 && (
          <Text variant="body2" className="whitespace-nowrap tabular-nums">
            {`Avg: ${averages.join('  \u2022  ')}`}
          </Text>
        )}
      </div>
    </Card>
  )
}

/**
 * Gas Savings in its own card: savings vs. an equivalent gas car over the
 * range. The ? icon opens the same GasSavingsDialog used by the legacy
 * card, so gas-car details stay editable in one place.
 */
function GasSavingsCard({
  sessions,
  vehicleId,
}: {
  sessions: PlugSessions
  vehicleId?: number
}) {
  const dispatch = useAppDispatch()
  const [isGasDialogOpen, setGasDialogOpen] = useState(false)
  // The dialog saves a new comparable car (mpg) and/or gas price; this
  // card's number is computed server-side from both, so refetch or the card
  // keeps showing the pre-edit savings. Stale-while-refetch keeps the old
  // value on screen until the repriced one lands.
  const onGasDetailsSaved = () => {
    setGasDialogOpen(false)
    dispatch(plugSessionsCollection.actions.invalidate())
  }
  return (
    <Card fullWidth className="box-border">
      <div className="flex flex-col gap-1 h-auto items-center text-center">
        <div className="flex items-center gap-1.5">
          <Text variant="body1">Gas Savings</Text>
          <button
            aria-label="Edit gas car details"
            onClick={() => setGasDialogOpen(true)}
            // No global preflight: strip the UA button chrome explicitly
            className="text-themed-grey-900 bg-transparent border-0 p-0 flex items-center"
          >
            <HelpCircle size={14} />
          </button>
        </div>
        <Text variant="h1" className="tabular-nums">
          {formatWholeCurrency(sessions.gas_savings ?? 0, sessions.currency)}
        </Text>
        <Text variant="body2">vs an ICE vehicle</Text>
      </div>
      {/* Mount only while open — the dialog renders its own loading state
          otherwise, which would sit inside the card */}
      {isGasDialogOpen && vehicleId !== undefined && (
        <GasSavingsDialog
          open
          onClose={() => setGasDialogOpen(false)}
          onSubmit={onGasDetailsSaved}
          onSubmitPressed={() => setGasDialogOpen(false)}
          initialVehicleId={vehicleId}
        />
      )}
    </Card>
  )
}

/**
 * Total energy over the filtered range with a per-charge average, so the
 * "weekly/monthly kWh" survey asks have a home (pair it with the This
 * Month / Past 30 Days presets).
 */
function TotalEnergyCard({ sessions }: { sessions: PlugSessions }) {
  const average =
    sessions.count > 0
      ? Math.round((sessions.total_energy_kwh / sessions.count) * 10) / 10
      : null
  return (
    <Card fullWidth className="box-border">
      <div className="flex flex-col gap-1 h-auto items-center text-center">
        <Text variant="body1">Total Energy</Text>
        <Text variant="h1" className="tabular-nums">
          {`${Math.round(sessions.total_energy_kwh).toLocaleString(
            'en-US',
          )} kWh`}
        </Text>
        {average !== null && (
          <Text variant="body2">{`Avg: ${average} kWh per charge`}</Text>
        )}
      </div>
    </Card>
  )
}

/**
 * First-load state: grey placeholders in the exact shape of the loaded
 * section, so the layout is "there" before the data is and nothing shifts
 * when it lands. The heading renders real. The date picker and preset chips
 * are placeholders ON PURPOSE — the picker's default range comes from the
 * loaded envelope (oldest session -> today), so a live picker here would
 * show empty/wrong dates and invite filter requests before the base query
 * returns. animate-pulse is motion-safe: reduced-motion users get static
 * placeholders.
 */
export function ChargeSessionsSkeleton({
  vehicleName,
}: {
  vehicleName?: string
}) {
  const block = 'motion-safe:animate-pulse bg-themed-grey-200 rounded-lg'
  return (
    <div
      className="flex flex-col gap-4 w-full"
      aria-busy="true"
      aria-label="Loading charge sessions"
    >
      <div className="flex flex-col gap-0.5">
        <div className="flex items-center gap-2">
          <Text variant="h3">Charges</Text>
          <span className="bg-themed-purple-200 rounded-full px-2 py-0.5">
            <Text variant="body4" className="!text-themed-purple-500 font-bold">
              New
            </Text>
          </span>
        </div>
        {vehicleName && <Text variant="body2">{vehicleName}</Text>}
      </div>
      {/* Date fields (51px matches the real RangeInput card height) */}
      <div className="flex gap-2">
        <div className={`${block} flex-1 h-[51px]`} />
        <div className={`${block} flex-1 h-[51px]`} />
      </div>
      {/* Preset chips */}
      <div className="flex justify-end gap-2">
        <div className={`${block} !rounded-full w-16 h-7`} />
        <div className={`${block} !rounded-full w-20 h-7`} />
        <div className={`${block} !rounded-full w-24 h-7`} />
      </div>
      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className={`${block} h-24`} />
        <div className={`${block} h-24`} />
        <div className={`${block} h-24`} />
      </div>
      {/* Session cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className={`${block} h-36`} />
        <div className={`${block} h-36`} />
      </div>
    </div>
  )
}

export type PlugSessionsViewProps = {
  sessions: PlugSessions
  onPageChange: (page: number) => void
  startDate: string | null
  endDate: string | null
  onDateRangeChange: (start: string | null, end: string | null) => void
  isFiltered: boolean
  onResetDateRange: () => void
  onAddRatePlan?: () => void
  /** Needed by the gas-savings dialog to edit the right vehicle's conversion */
  vehicleId?: number
  /** Slot for the feature-feedback banner — injected by the page wrapper so
   *  this view (and its stories) stay free of redux/survey wiring */
  feedbackBanner?: React.ReactNode
  /** True while a refetch is in flight with previous data still shown */
  isRefreshing?: boolean
  /** "Plug Sessions for {name}" — the car's custom name or 'YY Make Model.
   *  Omitted (charger sections, name unknown) the heading stays plain. */
  vehicleName?: string
  /** The vehicle's connection looks dead (stale last_ping) — the empty
   *  state coaches reconnecting instead of "plug in your car" */
  isConnectionBroken?: boolean
  onFixConnection?: () => void
}

export function PlugSessionsView({
  sessions,
  onPageChange,
  startDate,
  endDate,
  onDateRangeChange,
  isFiltered,
  onResetDateRange,
  onAddRatePlan,
  vehicleId,
  feedbackBanner,
  isRefreshing = false,
  vehicleName,
  isConnectionBroken = false,
  onFixConnection,
}: PlugSessionsViewProps) {
  const navigation = useNavigation()
  const showCosts = sessions.has_rate_plan

  // Load-more shares isRefreshing with filter changes — this flag remembers
  // which one caused the refetch so the button (not the filter pill) shows
  // the loading state. Cleared when the refetch lands or filters change.
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  useEffect(() => {
    if (!isRefreshing) {
      setIsLoadingMore(false)
    }
  }, [isRefreshing])

  const presets = presetRanges()
  // Which preset chip (if any) matches the current range exactly. A custom
  // range matches none and renders as a removable date chip in the same row.
  const activePreset: PresetKey | null = !isFiltered
    ? 'all'
    : startDate === presets.month[0] && endDate === presets.month[1]
    ? 'month'
    : startDate === presets.past30[0] && endDate === presets.past30[1]
    ? 'past30'
    : null
  const onPreset = (key: PresetKey) => {
    setIsLoadingMore(false)
    if (key === 'all') {
      onResetDateRange()
    } else {
      onDateRangeChange(presets[key][0], presets[key][1])
    }
  }

  // The detail (L2) is a real route, not an inline swap — tapping a card
  // navigates away from the dashboard and back returns to it
  const onSelect = (session: PlugSession) =>
    navigation.push(`/plug-sessions/${session.id}/details`)

  const isEmpty = sessions.results.length === 0

  return (
    <div className="flex flex-col gap-4 w-full">
      <div className="flex flex-col gap-0.5">
        <div className="flex items-center gap-2">
          <Text variant="h3">Charges</Text>
          <span className="bg-themed-purple-200 rounded-full px-2 py-0.5">
            <Text variant="body4" className="!text-themed-purple-500 font-bold">
              New
            </Text>
          </span>
        </div>
        {/* Which car this section covers — the custom name or 'YY Make Model.
            Requested twice in survey feedback (easy screenshots, multi-EV
            households). Charger sections have no vehicleName and skip it. */}
        {vehicleName && <Text variant="body2">{vehicleName}</Text>}
      </div>

      {feedbackBanner}

      {/* Hide the picker only for a genuinely session-less account — if a
          user's own filter emptied the list, hiding it would trap them */}
      {(!isEmpty || isFiltered) && (
        <>
          <DateRangePicker
            id="plug-sessions-date-range"
            startDate={startDate}
            endDate={endDate}
            onChange={onDateRangeChange}
          />
          {/* Refetch pill sits centered between the date inputs and the
              preset chips while a filter change is in flight — the list
              keeps its stale data, so this is the signal the new filter is
              being applied */}
          {isRefreshing && !isLoadingMore && (
            <div className="flex justify-center -mt-1">
              <span className="flex items-center gap-2 bg-themed-grey-900 rounded-full px-3 py-1">
                <CircularProgress size={12} style={{ color: 'white' }} />
                <Text variant="body4" className="!text-themed-white">
                  Filtering by date…
                </Text>
              </span>
            </div>
          )}
          <div className="flex flex-wrap justify-end gap-2">
            {(Object.keys(PRESET_LABELS) as PresetKey[]).map((key) => (
              <PresetChip
                key={key}
                label={PRESET_LABELS[key]}
                active={activePreset === key}
                onClick={() => onPreset(key)}
              />
            ))}
            {/* A custom (typed/picked) range renders as its own removable
                chip in the SAME row, active-styled; X clears back to All
                Time. Hidden while its refetch is in flight — the pill below
                announces that state. */}
            {!isRefreshing && isFiltered && activePreset === null && (
              <button
                aria-label="Clear date filter"
                onClick={onResetDateRange}
                className="flex items-center gap-1.5 bg-themed-grey-900 rounded-full px-3 py-1 border-0 cursor-pointer"
              >
                <Text variant="body4" className="!text-themed-white">
                  {`${formatChipDate(startDate)} – ${formatChipDate(endDate)}`}
                </Text>
                <Icon name="X" size={14} color="white" />
              </button>
            )}
          </div>
        </>
      )}

      {/* The rate-plan CTA shows even before the first session (AC 2) —
          it's a good prompt while they wait */}
      {!showCosts && <NoRatePlanAlert onAddRatePlan={onAddRatePlan} />}

      {!isEmpty && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Row 1: money. Row 2: energy + counts. Without a rate plan the
              money cards drop and the second row becomes the only row. */}
          {showCosts && sessions.total_cost !== null && (
            <TotalSpentCard sessions={sessions} />
          )}
          {showCosts && sessions.total_cost !== null && (
            <GasSavingsCard sessions={sessions} vehicleId={vehicleId} />
          )}
          <TotalEnergyCard sessions={sessions} />
          <SessionsSummaryCard sessions={sessions} />
        </div>
      )}

      {isEmpty && (
        <Card fullWidth className="box-border">
          <div className="flex flex-col items-center gap-2 py-4 text-center">
            <div className="w-10 h-10 bg-themed-grey-100 rounded-lg flex items-center justify-center">
              <Icon name="BatteryCharging" size={22} color="grey-500" />
            </div>
            {isFiltered ? (
              <>
                <Text variant="body1">No charge sessions to show</Text>
                <Text variant="body4">
                  Remove or change the date filters above to see more charge
                  sessions
                </Text>
              </>
            ) : isConnectionBroken ? (
              <>
                <Text variant="body1">No charge sessions yet</Text>
                {/* Dead vehicle connection (stale last_ping): "plug in your
                    car" is false coaching — nothing will appear until the
                    connection is fixed */}
                <AlertMessage variant="warning">
                  {'Fix your vehicle connection to see charge insights. '}
                  <span
                    className="underline underline-offset-2 cursor-pointer font-semibold"
                    onClick={onFixConnection}
                  >
                    Fix Now
                  </span>
                </AlertMessage>
              </>
            ) : (
              <>
                <Text variant="body1">No charge sessions yet</Text>
                <Text variant="body4">
                  Plug in your car to see your first charge session
                </Text>
              </>
            )}
          </div>
        </Card>
      )}

      {/* Two columns on bigger screens, matching the legacy card layout */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {sessions.results.map((session) => (
          <PlugSessionCard
            key={session.id}
            session={session}
            showCosts={showCosts}
            onSelect={onSelect}
          />
        ))}
      </div>

      {/* Hidden once the last page is loaded — pages accumulate in the
          view model, so page_number tracks the deepest page fetched */}
      {sessions.page_number < sessions.total_pages && (
        <Button
          id="plug-sessions-load-more"
          variant="primary"
          disabled={isLoadingMore}
          onClick={() => {
            setIsLoadingMore(true)
            onPageChange(sessions.page_number + 1)
          }}
        >
          {isLoadingMore ? 'Loading more charges…' : 'Load more charges'}
        </Button>
      )}
    </div>
  )
}

export default function PlugSessionsPage({
  vehicleId,
  chargerId,
}: {
  vehicleId?: number
  chargerId?: number
}) {
  const navigation = useNavigation()
  const vehicle = useAppSelector((state) => {
    if (vehicleId === undefined) {
      return undefined
    }
    return state.vehicles.vehicles?.find((v) => v.id === vehicleId)
  })
  // The car's custom name, else "'24 Tesla Model Y". Charger sections
  // (chargerId) and unnamed/unloaded vehicles keep the plain heading.
  const carModel = vehicle?.car.car_model
  // Makes are stored lowercased for some providers ("tesla") — capitalize
  // the first letter only, so "BMW" stays "BMW"
  const make = carModel
    ? carModel.make.charAt(0).toUpperCase() + carModel.make.slice(1)
    : ''
  const vehicleName =
    vehicle?.car.display_name ||
    (carModel
      ? `'${carModel.year.slice(-2)} ${make} ${
          carModel.friendly_model || carModel.model
        }`
      : undefined)
  // No ping in 14 days (junghee's 2024-dead vehicle: zero charges ever) —
  // the generic "plug in your car" empty state is false coaching there
  const isConnectionBroken =
    !!vehicle &&
    (!vehicle.last_ping || dayjs().diff(dayjs(vehicle.last_ping), 'day') >= 14)
  const {
    sessions,
    isLoading,
    hasError,
    onPageChange,
    startDate,
    endDate,
    onDateRangeChange,
    isFiltered,
    onResetDateRange,
  } = useViewModel({ vehicleId, chargerId })

  if (hasError && !sessions) {
    return (
      <div className="flex flex-col gap-4 w-full">
        <Text variant="h3">Charges</Text>
        <AlertMessage variant="warning">
          We couldn&apos;t load your charges. Please try again in a moment.
        </AlertMessage>
      </div>
    )
  }

  // Skeleton only on the FIRST load. Refetches (date-filter changes,
  // Load more) keep showing the previous data via the view model's
  // stale-while-refetch envelope — unmounting the whole section on every
  // refresh made the feature "disappear" (customer feedback). isLoading
  // alone must never blank the section.
  if (!sessions) {
    return <ChargeSessionsSkeleton vehicleName={vehicleName} />
  }

  return (
    <PlugSessionsView
      sessions={sessions}
      onPageChange={onPageChange}
      startDate={startDate}
      endDate={endDate}
      onDateRangeChange={onDateRangeChange}
      isFiltered={isFiltered}
      onResetDateRange={onResetDateRange}
      onAddRatePlan={() => navigation.push('/connect-utility/select')}
      vehicleId={vehicleId}
      vehicleName={vehicleName}
      isConnectionBroken={isConnectionBroken}
      onFixConnection={() =>
        navigation.push(`/devices/vehicle/${vehicleId}/connectivity`)
      }
      isRefreshing={isLoading}
      feedbackBanner={
        <FeatureFeedbackBanner
          surveyName="Plug Sessions Feedback 1"
          question="What do you think about this updated feature?"
        />
      }
    />
  )
}
